import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Grid,
  Button,
  Card,
  Chip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  InputAdornment,
  Tooltip,
  Badge
} from '@mui/material';
import {
  DataGrid,
  gridClasses
} from '@mui/x-data-grid';
import {
  Search,
  FilterList,
  Download,
  CalendarToday,
  Phone,
  Person,
  Apartment,
  Visibility,
  Refresh,
  Clear,
  Business,
  Home,
  Villa
} from '@mui/icons-material';
import { vendorApi } from '../../api/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

// Status configuration with colors and icons
const statusConfig = {
  Scheduled: { color: 'primary', label: 'Scheduled', variant: 'filled' },
  completed: { color: 'success', label: 'Completed', variant: 'filled' },
  cancelled: { color: 'error', label: 'Cancelled', variant: 'filled' },
  confirmed: { color: 'info', label: 'Confirmed', variant: 'filled' },
  pending: { color: 'warning', label: 'Pending', variant: 'filled' }
};

// Property type configuration with colors and icons
const propertyTypeConfig = {
  apartment: { color: 'primary', label: 'Apartment', icon: <Apartment /> },
  project: { color: 'success', label: 'Project', icon: <Business /> },
  property: { color: 'warning', label: 'Property', icon: <Home /> },
  villa: { color: 'secondary', label: 'Individual Villa', icon: <Villa /> }
};

// Helper function to get the best property display name
const getPropertyDisplayName = (row) => {
  // Priority order for property name display
  if (row.ApartmentName && row.ApartmentName !== 'N/A') return row.ApartmentName;
  if (row.propertyName && row.propertyName !== 'N/A') return row.propertyName;
  if (row.project_name && row.project_name !== 'N/A') return row.project_name;
  return 'Property Name Not Available';
};

// Helper function to get property type display with fallback
const getPropertyTypeInfo = (row) => {
  const type = row.PropertyType?.toLowerCase() || '';
  
  // Direct mapping for your 4 types
  if (type.includes('apartment')) return propertyTypeConfig.apartment;
  if (type.includes('project')) return propertyTypeConfig.project;
  if (type.includes('villa')) return propertyTypeConfig.villa;
  if (type.includes('property')) return propertyTypeConfig.property;
  
  // Infer from available data
  if (row.ApartmentName) return propertyTypeConfig.apartment;
  if (row.project_name) return propertyTypeConfig.project;
  if (row.propertyName && row.propertyName.includes('Villa')) return propertyTypeConfig.villa;
  
  // Default fallback to Property
  return propertyTypeConfig.property;
};

// Helper function to get project/subtitle
const getPropertySubtitle = (row) => {
  const propertyName = getPropertyDisplayName(row);
  const typeInfo = getPropertyTypeInfo(row);
  
  // Show different subtitles based on property type
  switch(typeInfo.label) {
    case 'Apartment':
      return row.project_name || row.propertyName || 'Apartment Building';
    case 'Project':
      return row.propertyName || 'Real Estate Project';
    case 'Individual Villa':
      return row.project_name || 'Individual Villa';
    case 'Property':
      return row.project_name || 'Real Estate Property';
    default:
      return 'Real Estate';
  }
};

const columns = [
  {
    field: 'id',
    headerName: 'ID',
    width: 80,
    headerAlign: 'center',
    align: 'center'
  },
  {
    field: 'userInfo',
    headerName: 'USER',
    width: 200,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          <Person fontSize="small" />
        </Avatar>
        <Box>
          <Typography variant="subtitle2" noWrap>
            {params.row.username || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.mobilenumber || 'No phone'}
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    field: 'propertyInfo',
    headerName: 'PROPERTY DETAILS',
    width: 250,
    renderCell: (params) => {
      const typeInfo = getPropertyTypeInfo(params.row);
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              bgcolor: `${typeInfo.color}.main`,
              mt: 0.5
            }}
          >
            {React.cloneElement(typeInfo.icon, { fontSize: 'small' })}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant="subtitle2" 
              noWrap 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.2,
                mb: 0.5
              }}
            >
              {getPropertyDisplayName(params.row)}
            </Typography>
            
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                display: 'block',
                lineHeight: 1.2,
              }}
            >
              {getPropertySubtitle(params.row)}
            </Typography>
          </Box>
        </Box>
      );
    }
  },
  {
    field: 'PropertyType',
    headerName: 'PROPERTY TYPE',
    width: 150,
    renderCell: (params) => {
      const typeInfo = getPropertyTypeInfo(params.row);
      return (
        <Chip
          icon={React.cloneElement(typeInfo.icon, { fontSize: 'small' })}
          label={typeInfo.label}
          color={typeInfo.color}
          variant="filled"
          size="small"
          sx={{ 
            fontWeight: 600,
            '& .MuiChip-label': { px: 1 }
          }}
        />
      );
    }
  },
  {
    field: 'scheduledDateTime',
    headerName: 'VISIT DATE & TIME',
    width: 180,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarToday color="action" fontSize="small" />
        <Box>
          <Typography variant="body2" noWrap>
            {dayjs(params.value).format('DD MMM YYYY')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(params.value).format('hh:mm A')}
          </Typography>
        </Box>
      </Box>
    )
  },
  {
    field: 'status',
    headerName: 'STATUS',
    width: 130,
    renderCell: (params) => {
      const config = statusConfig[params.value] || { color: 'default', label: params.value };
      return (
        <Chip
          label={config.label}
          color={config.color}
          variant={config.variant}
          size="small"
        />
      );
    }
  },
  {
    field: 'actions',
    headerName: 'ACTIONS',
    width: 100,
    sortable: false,
    renderCell: (params) => (
      <Tooltip title="View Details">
        <IconButton
          size="small"
          onClick={() => console.log('View details:', params.row)}
          color="primary"
        >
          <Visibility />
        </IconButton>
      </Tooltip>
    )
  }
];

const SiteVisit = () => {
  const [siteVisits, setSiteVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [pageSize,setPageSize]=useState(25);
  useEffect(() => {
    fetchSiteVisits();
  }, []);

  const fetchSiteVisits = async () => {
    try {
      setLoading(true);
      const res = await vendorApi.getAllSiteVisits();
      const data = res.data || [];
      
      // Clean up the data
      const cleanedData = data.map(visit => ({
        ...visit,
        ApartmentName: visit.ApartmentName || null,
        propertyName: visit.propertyName || null,
        project_name: visit.project_name || null,
        PropertyType: visit.PropertyType || null,
        status: visit.status || 'pending'
      }));
      
      setSiteVisits(cleanedData);
      setFilteredVisits(cleanedData);
    } catch (error) {
      console.error('Failed to fetch site visit data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = siteVisits;

    // Date filter
    if (filterDate) {
      filtered = filtered.filter((visit) => {
        const visitDate = dayjs(visit.scheduledDateTime).format('YYYY-MM-DD');
        return visitDate === filterDate;
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((visit) => 
        visit.status === statusFilter
      );
    }

    // Property type filter
    if (propertyTypeFilter !== 'all') {
      filtered = filtered.filter((visit) => {
        const typeInfo = getPropertyTypeInfo(visit);
        return typeInfo.label.toLowerCase() === propertyTypeFilter.toLowerCase();
      });
    }

    // Search filter
    if (searchKeyword.trim() !== '') {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(
        (visit) =>
          visit.username?.toLowerCase().includes(keyword) ||
          visit.propertyName?.toLowerCase().includes(keyword) ||
          visit.ApartmentName?.toLowerCase().includes(keyword) ||
          visit.project_name?.toLowerCase().includes(keyword) ||
          visit.mobilenumber?.toString().includes(keyword) ||
          visit.PropertyType?.toLowerCase().includes(keyword)
      );
    }

    setFilteredVisits(filtered);
  }, [filterDate, searchKeyword, statusFilter, propertyTypeFilter, siteVisits]);

  const handleExportToExcel = () => {
    const exportData = filteredVisits.map((visit) => ({
      ID: visit.id,
      Username: visit.username,
      'Project Name': visit.project_name,
      'Apartment Name': visit.ApartmentName,
      'Property Name': visit.propertyName,
      'Property Type': visit.PropertyType,
      'Mobile Number': visit.mobilenumber,
      'Scheduled Date': dayjs(visit.scheduledDateTime).format('DD MMM YYYY'),
      'Scheduled Time': dayjs(visit.scheduledDateTime).format('hh:mm A'),
      Status: visit.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SiteVisits');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, `SiteVisits_${dayjs().format('DD-MM-YYYY')}.xlsx`);
  };

  const clearFilters = () => {
    setFilterDate('');
    setSearchKeyword('');
    setStatusFilter('all');
    setPropertyTypeFilter('all');
  };

  const getStatusCount = (status) => {
    return siteVisits.filter(visit => visit.status === status).length;
  };

  // Get unique property types for filter (only your 4 types)
  const getUniquePropertyTypes = () => {
    return ['Apartment', 'Project', 'Property', 'Individual Villa'];
  };

  const totalVisits = siteVisits.length;
  const filteredCount = filteredVisits.length;

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">
          Site Visit Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and track all site visit requests in one place
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {totalVisits}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Visits
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {getStatusCount('Scheduled')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scheduled
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {getStatusCount('completed')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {getStatusCount('pending')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
            <Typography variant="h4" fontWeight="bold" color="error.main">
              {getStatusCount('cancelled')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cancelled
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Card */}
      <Card elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Filter by Date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Filter by Status"
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList color="action" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="Property Type"
              fullWidth
              size="small"
              value={propertyTypeFilter}
              onChange={(e) => setPropertyTypeFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Home color="action" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              {getUniquePropertyTypes().map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8} md={4}>
            <TextField
              label="Search..."
              placeholder="Search by name, property, mobile..."
              fullWidth
              size="small"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchKeyword && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchKeyword('')}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh Data">
                <IconButton onClick={fetchSiteVisits} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportToExcel}
                color="primary"
                fullWidth
                sx={{ py: 1 }}
              >
                Export
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Results Count */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredCount} of {totalVisits} visits
          </Typography>
          {(filterDate || statusFilter !== 'all' || propertyTypeFilter !== 'all' || searchKeyword) && (
            <Chip
              label="Filters Active"
              color="primary"
              variant="outlined"
              size="small"
              onDelete={clearFilters}
            />
          )}
        </Box>
      </Card>

      {/* Data Grid */}
      <Card elevation={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredVisits}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[25,50,100]}
              onPageSizeChange={(newSize)=>setPageSize(newSize)}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          )}
        </Box>
      </Card>

      {/* Detail Dialog (You can implement this later) */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Site Visit Details</DialogTitle>
        <DialogContent>
          {/* Add detailed view content here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteVisit;